import {Header, Layout} from '@components/common'
import {createPopulationQuery, fetchJson, getShopNavigation, redirectTo308, redirectTo404Page} from "./index";
import {InferGetServerSidePropsType} from "next";
import {serverSideTranslations} from "next-i18next/serverSideTranslations";
import commerce from "@lib/api/commerce";
import Hero from "@components/CmsBlocks/Hero";
import FeaturedPodcast from "@components/CmsBlocks/FeaturedPodcast";
import FeaturedVideo from "@components/CmsBlocks/FeaturedVideo";
import ProductGallery from "@components/CmsBlocks/ProductGallery";
import ShopFoldContent from "@components/CmsBlocks/ShopFoldContent";
import Banner from "@components/CmsBlocks/banner";
import {useTranslation} from "next-i18next";
import Head from "next/head";
import SponsorGallery from "@components/CmsBlocks/SponsorGallery";
import {productConnectionFragment} from "@framework/utils/queries/get-all-products-query";
import {normalizeProduct} from '@framework/utils';
import Gallery from "@components/CmsBlocks/Gallery";
import History from "@components/CmsBlocks/History";
import ImageSlider from "@components/CmsBlocks/ImageSlider";
import BlogGallery from "@components/CmsBlocks/BlogGallery";
import FeaturedContent from "@components/CmsBlocks/FeaturedContent";

const getProductsByCollectionQuery = /* GraphQL */ `
  query getAllProducts(
    $first: Int = 250,
    $collection: String!
  ) {
    collection(handle: $collection) {
      products(
        first: $first
      ) {
        ...productConnection
      }
    }
  }

  ${productConnectionFragment}
`;


export interface Title {
  _content: string;
}

export interface Description {
  _content: string;
}

export interface PrimaryPhotoExtras {
  url_sq: string;
  height_sq: number;
  width_sq: number;
  url_t: string;
  height_t: number;
  width_t: number;
  url_s: string;
  height_s: number;
  width_s: number;
  url_q: string;
  height_q: number;
  width_q: number;
  url_m: string;
  height_m: number;
  width_m: number;
  url_n: string;
  height_n: number;
  width_n: number;
  url_z: string;
  height_z: number;
  width_z: number;
  url_c: string;
  height_c: number;
  width_c: number;
  url_l: string;
  height_l: number;
  width_l: number;
  url_o: string;
  height_o: number;
  width_o: number;
}

export interface Photoset {
  id: string;
  owner: string;
  username: string;
  primary: string;
  secret: string;
  server: string;
  farm: number;
  count_views: string;
  count_comments: string;
  count_photos: number;
  count_videos: number;
  title: Title;
  description: Description;
  can_comment: number;
  date_create: string;
  date_update: string;
  photos: number;
  videos: number;
  visibility_can_see_set: number;
  needs_interstitial: number;
  primary_photo_extras: PrimaryPhotoExtras;
}

export interface Photosets {
  page: number;
  pages: number;
  perpage: number;
  total: number;
  photoset: Photoset[];
}

export interface FlickrAlbum {
  photosets: Photosets;
}

async function getProductsByCollection(
  { query = getProductsByCollectionQuery , collection , config: cfg }: any
) {
  const { fetch , locale  } = commerce.getConfig(cfg);
  let { data } = await fetch(query, {
    variables: {collection: collection}
  }, {
    ...locale && {
      headers: {
        "Accept-Language": locale
      }
    }
  });
  if (
    !data ||
    !data.collection ||
    !data.collection.products ||
    !data.collection.products.edges
  ) {
    return {
      products: []
    }
  }
  return {
    ...data.collection.products.edges && {
      products: data.collection.products.edges.map(({node}: any) => {
        return normalizeProduct(node);
      })
    }
  };
}

const getProductData = (slug: any = null, collection = null, locale: any = null) => {
  return new Promise(async (resolve) => {
    const config = {
      locale: locale
    }
    const preview = false;
    if (!slug && !collection) {
      const {products} = await commerce.getAllProducts({
        variables: {first: 12},
        config,
      });
      resolve(products);
    } else if (slug) {
      const { product } = await commerce.getProduct({
        variables: { slug: slug},
        config,
        preview,
      })

      resolve(product);
    } else if (collection) {
      const {products} = await getProductsByCollection({
        collection:  collection,
        config,
      });
      console.log("getProductsByCollection", products);
      resolve(products);
    } else {
      resolve(null);
    }

  });
};

export async function getServerSideProps(context: any) {

  const {locale, params} = context;
  const url = `${process.env.NEXT_PUBLIC_API_URL || "http://localhost:1337"}`;
  const populationQuery = createPopulationQuery([
    '*',
    'blocks',
    'blocks.link',
    'blocks.image',
    'blocks.Image',
    'blocks.Images',
    'blocks.Media',
    'blocks.Text',
    'blocks.CTA',
    'blocks.Background',
    'blocks.Media.Title',
    'blocks.Media.CoverImage',
    'blocks.Products',
    'blocks.Collection',
    'blocks.Products.Collection',
    'blocks.Media.image_gallery',
    'blocks.Media.image_gallery.data',
    'blocks.Media.image_gallery.Title',
    'blocks.Media.image_gallery.Media',
    'blocks.Gallery',
    'blocks.Cover',
    'blocks.Columns',
    'blocks.Columns.Image',
    'blocks.Column',
    'blocks.Column.Image',
    'SEO',
    'Recommendations',
    'Recommendations.Cover',
    'Recommendations.Cover',
  ]);
  const uri = `/api/pages?${populationQuery}&locale=${locale}&filters[slug][$eq]=${params!.page.toLowerCase()}`;
  console.log("URI!!!!", uri);
  try {
    const {
      Title,
      SEO,
      blocks,
    }: any = await fetchJson(url + uri, 'data.0.attributes');

    // This checks if there are any blocks that require product data
    // and if so, fetches it server side, and adds it to the block object
    // so it can be passed down as a prop to the component. This is mostly
    // because components themselves do not have a getServerSideProps function
    if (blocks && blocks.length > 0) {
      let final_promises: any = [];
      blocks.forEach((data: any) => {
        let promises = [];
        switch (data.__component) {
          case 'blocks.image-gallery':
            const flickrApiKey = "3849884059e15ddd371a2300cb308d10";
            const method = "flickr.photosets.getInfo";
            const userId = "125658326@N04";
            const extras = "url_sq,url_t,url_s,url_q,url_m,url_n,url_z,url_c,url_l,url_o";
            promises.push(new Promise(async (resolve) => {
              data.albumData = [];
              Promise.all(data.Gallery.map(async (x: {flickrId: string}) => {
                // For each gallery get data such as name and cover image
                const url = "https://api.flickr.com/services/rest/?method=" + method + "&api_key=" + flickrApiKey + "&user_id=" + userId + "&photoset_id=" + x.flickrId + "&format=json&primary_photo_extras=" + extras + "&nojsoncallback=1"
                const res = await fetchJson(url);
                data.albumData.push(res);
                resolve(null);
              })).then(() => {
                resolve(null);
              });
            }));
            break;
          case 'blocks.product-gallery':
            promises.push(new Promise(async (resolve) => {
              data.productData = await getProductData(null, data.Products.Collection, locale);
              resolve(null);
            }));
            break;
          case 'blocks.shop-fold-content':
            promises.push(new Promise(async (resolve) => {
              /* todo parallel load these products */
              const product1 = await getProductData(data.FeaturedProduct1, null, locale);
              const product2 = await getProductData(data.FeaturedProduct2, null, locale);
              const product3 = await getProductData(data.FeaturedProduct3, null, locale);
              if (!data.productData) {
                data.productData = [];
              }
              product1 && data.productData.push(product1);
              product2 && data.productData.push(product2);
              product3 && data.productData.push(product3);
              resolve(null);
            }));
            break;
          case 'blocks.blog-gallery':
            if (!data.blogArticles) {
              data.blogArticles = [];
            }
            const uri = `/api/articles?populate=*&locale=${locale}&filters[Tags][$containsi]=${data.Tag}&pagination[page]=1&pagination[pageSize]=6&sort[0]=Date%3Adesc`;
            promises.push(new Promise(async (resolve) => {
              data.blogArticles = await fetchJson(url + uri, 'data');
              resolve(null);
            }));
            break;
        }
        if (promises) {
          final_promises = [...final_promises, ...promises];
        }
      });
      await Promise.all(final_promises);
    }

    return {
      props: {
        ...(await serverSideTranslations(locale, ["common", "home"])),
        ...(await getShopNavigation(locale)), // makes "ShopNavigationColumn" property available on page props
        page: params.page,
        title: Title,
        seo: SEO,
        blocks
      }
    }

  } catch (e) {
    console.error(e);
    return  redirectTo404Page();
  }
}

export default function Page({
                               title,
                               seo,
                               blocks,
                               sponsors
                             }: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const { t } = useTranslation("common");
  return (
    <div>
      <Head>
        <>
          <title>{title}</title>
          {
            () => {
              if (seo) {
                <meta name="description" content={seo?.metaDescription} key="description"></meta>
                {
                  seo.SeoMetaRepeatableComponent && seo.SeoMetaRepeatableComponent.map(
                    ({ id, name, content, structuredData}: any, index: any) => {
                      return <meta key={name} name={name} content={content}></meta>
                    })
                }
              }
            }
          }
          </>
      </Head>

      <div className="container-rg mb-6">
        <Header></Header>
        { blocks && blocks.map((block: any, index: any) => {
          return renderBlock(block, index, block.productData, block.blogArticles, block.albumData)
        })}
      </div>
      { sponsors && <SponsorGallery { ...sponsors } />}
    </div>
  )
}

export const renderBlock = ({ __component, ...data }: any, index: any, products = null, blogArticles = null, albums = null) => {
  let Block;
  switch (__component) {
    case 'blocks.hero':
      Block = Hero;
      break;
    case 'blocks.history':
      Block = History;
      break;
    case 'blocks.featured-podcast':
      Block = FeaturedPodcast;
      break;
    case 'blocks.featured-video':
      Block = FeaturedVideo;
      break;
    case 'blocks.image-gallery':
      return <Gallery key={`block-index-${index}`} albums={albums}></Gallery>;
      break;
    case 'blocks.image-slider':
      Block = ImageSlider;
      break;
    case 'blocks.featured-content':
      Block = FeaturedContent;
      break;
    case 'blocks.blog-gallery':
      return <BlogGallery key={`block-index-${index}`} title={data.Title} articles={blogArticles}></BlogGallery>;
      break;
    case 'blocks.text':
      return <span dangerouslySetInnerHTML={{__html: data.Text}}></span>
      break;
    case 'blocks.product-gallery':
      return <ProductGallery key={`block-index-${index}`} products={products} />
      break;
    case 'blocks.shop-fold-content':
      if (products) {
        /* @ts-ignore*/
        data.FeaturedProduct1 = products[0];
        /* @ts-ignore*/
        data.FeaturedProduct2 = products[1];
        /* @ts-ignore*/
        data.FeaturedProduct3 = products[2];
      }
      Block = ShopFoldContent;
      break;
    case 'blocks.banner':
      Block = Banner;
      break;
  }
  /* @ts-ignore */
  return Block ? <Block key={`block-index-${index}`} {...data} /> : null;
};

Page.Layout = Layout
